/**
 * PREVIEW PARITY REWORK (2026-07-28 ruling): mirrors backend/utils/
 * document_generator.py's Commercial Requirements Schedule helpers
 * EXACTLY - _truncate_schedule_description, _line_is_goods/
 * _line_is_services, _classify_goods_services, _VENDOR_PROPOSED_CELL_
 * TEXT, and the schedule's exact lead-in/note wording - so RfpPreview.jsx
 * renders the identical schedule the downloaded .docx does. Keep both
 * sides in sync: a change to any of the backend functions above needs
 * the matching change here (and vice versa).
 */

const normalizeWhitespace = (text) =>
  String(text || '').split(/\s+/).filter(Boolean).join(' ');

const SENTENCE_END_RE = /[.!?](?=\s|$)/g;
const DASH_SEGMENT_RE = / [-–—] /g;

const truncateAtWordBoundary = (text, maxLen) => {
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
};

// TRUNCATION MARKER + MINIMUM BOUNDARY FILL (Ashok rulings, 2026-08-22),
// mirrored here on 2026-08-23. The backend gained both in 63e58f9 and
// bcf445c; this twin got neither, because the pair was not in CLAUDE.md's
// mirror-pair registry and its only guard was the "keep both sides in
// sync" comment at the top of this file. The visible cost: the .docx
// rendered PROC-000006's line 1 as a 73-character abridgement ending in an
// ellipsis while this function returned the bare word "Laptop" with no
// marker, so the RFP PREVIEW - the surface a buyer reads before deciding
// to issue - under-represented what the vendor would receive.
//
// Both sides are now held together by a shared case table,
// backend/tests/fixtures/schedule_truncation_parity_cases.json, asserted
// by rfpSchedule.parity.test.js here and by
// backend/tests/test_schedule_truncation_marker.py there.
const SCHEDULE_TRUNCATION_MARKER = '…';
const SCHEDULE_MARKER_BUDGET = 2; // worst case: ' ' + marker
const SCHEDULE_MIN_BOUNDARY_FILL = 0.5;

// Python's round() is round-half-to-EVEN; JS's Math.round is round-half-UP.
// budget * 0.5 is only ever .0 or .5, so the two differ exactly when the
// budget is ODD. The production cap is 80 (budget 78, an exact 39), which
// hides the divergence until someone changes the cap - so reproduce
// Python's rule rather than relying on that. Pinned by the two cap-83 cases
// in the shared table.
const minBoundaryChars = (budget) => {
  const raw = budget * SCHEDULE_MIN_BOUNDARY_FILL;
  const floor = Math.floor(raw);
  if (raw - floor !== 0.5) return Math.round(raw);
  return floor % 2 === 0 ? floor : floor + 1;
};

// The marker, and the space it needs ONLY after terminal punctuation:
// "...tiles.…" reads as a typo rather than as elision, which would
// undermine the one thing the marker exists to do.
const appendTruncationMarker = (content) => {
  const trimmed = content.replace(/\s+$/, '');
  if (trimmed && '.!?'.includes(trimmed[trimmed.length - 1])) {
    return `${trimmed} ${SCHEDULE_TRUNCATION_MARKER}`;
  }
  return trimmed + SCHEDULE_TRUNCATION_MARKER;
};

// (1) full text if it already fits, with NO marker; (2) else the LAST
// sentence boundary within the content BUDGET (cap minus the marker's
// worst-case width), taken only if it carries at least half the budget;
// (3) else the LAST hyphen/en-dash/em-dash segment boundary, under the
// same threshold; (4) else a plain word boundary. Branches 2-4 all drop
// content, so all three carry the marker; branch 1 drops nothing and
// carries none.
export const truncateScheduleDescription = (text, cap = 80) => {
  const normalized = normalizeWhitespace(text);
  if (normalized.length <= cap) return normalized;

  const budget = cap - SCHEDULE_MARKER_BUDGET;
  const minBoundary = minBoundaryChars(budget);
  const window = normalized.slice(0, budget);

  const sentenceEnds = [];
  SENTENCE_END_RE.lastIndex = 0;
  let m;
  while ((m = SENTENCE_END_RE.exec(window)) !== null) {
    sentenceEnds.push(m.index + 1);
  }
  if (sentenceEnds.length > 0) {
    const cut = normalized.slice(0, sentenceEnds[sentenceEnds.length - 1]).replace(/\s+$/, '');
    if (cut.length >= minBoundary) return appendTruncationMarker(cut);
  }

  const dashPositions = [];
  DASH_SEGMENT_RE.lastIndex = 0;
  while ((m = DASH_SEGMENT_RE.exec(window)) !== null) {
    dashPositions.push(m.index);
  }
  if (dashPositions.length > 0) {
    const cut = normalized.slice(0, dashPositions[dashPositions.length - 1]).replace(/\s+$/, '');
    if (cut.length >= minBoundary) return appendTruncationMarker(cut);
  }

  return appendTruncationMarker(truncateAtWordBoundary(normalized, budget));
};

// DERIVED SCHEDULE NAME (Ashok ruling, 2026-08-23) - the JS twin of
// document_generator.py's _derive_schedule_name. Registered in CLAUDE.md's
// mirror-pair registry alongside its truncation sibling and held to it by
// the SAME shared case table,
// backend/tests/fixtures/schedule_truncation_parity_cases.json.
//
// ONE FIELD WAS SERVING TWO PURPOSES: the schedule cell needs a NAME a
// vendor can scan, Detailed Technical Specifications needs the
// SPECIFICATION, and `description` carries both. Truncation was what that
// cost. The refinement agent already writes both parts - "Laptop - Intel
// Core i7...", "High-back mesh ergonomic chair. Mesh back construction..."
// - and 91.6% of the measured corpus carries a usable boundary.
//
// NO MARKER ON A DERIVED NAME: it is COMPLETE IN ITSELF, so an ellipsis
// would assert an omission that did not happen at that boundary. The
// marker stays on the truncation fallback, where text genuinely was cut.
// And the 39-character fill threshold does NOT apply here - it governs
// truncateScheduleDescription and nothing else.
export const deriveScheduleName = (text, cap = 80) => {
  const normalized = normalizeWhitespace(text);
  if (!normalized) return '';

  SENTENCE_END_RE.lastIndex = 0;
  DASH_SEGMENT_RE.lastIndex = 0;
  const sentence = SENTENCE_END_RE.exec(normalized);
  const dash = DASH_SEGMENT_RE.exec(normalized);
  if (sentence === null && dash === null) {
    return truncateScheduleDescription(normalized, cap);
  }

  // A sentence boundary KEEPS its terminal punctuation; a dash boundary
  // does not keep the dash. Both match truncateScheduleDescription's own
  // two branches, so the derived name and the fallback cut in the same
  // places for the same reasons.
  let name;
  if (dash === null || (sentence !== null && sentence.index < dash.index)) {
    name = normalized.slice(0, sentence.index + 1).replace(/\s+$/, '');
  } else {
    name = normalized.slice(0, dash.index).replace(/\s+$/, '');
  }

  // A boundary at position 0 leaves nothing to name the line with. No such
  // row exists in the measured corpus (shortest derived name is 5), but a
  // blank pricing-schedule cell is worth guarding against outright.
  if (!name) return truncateScheduleDescription(normalized, cap);

  if (name.length <= cap) return name;
  return truncateScheduleDescription(name, cap);
};

export const lineIsGoods = (item) => {
  if (item.item_type === 'goods') return true;
  if (item.item_type === 'services') return false;
  return Boolean(item.need_by_date);
};

export const lineIsServices = (item) => {
  if (item.item_type === 'services') return true;
  if (item.item_type === 'goods') return false;
  return Boolean(item.start_date || item.end_date);
};

const CLASSIFY_KEYWORDS = ['amc', 'maintenance', 'service', 'support', 'contract', 'subscription'];

// Splits a mixed line-item list into goods/services groups - a line's own
// item_type wins, then date fields, then description keywords as a last
// resort. Same rule used to decide which pricing table a line belongs in.
export const classifyGoodsServices = (lineItems) => {
  const items = lineItems || [];
  const goodsItems = items.filter((item) => item.item_type === 'goods');
  const servicesItems = items.filter((item) => item.item_type === 'services');
  const unclassified = items.filter((item) => !goodsItems.includes(item) && !servicesItems.includes(item));

  for (const item of unclassified) {
    if (item.need_by_date && !(item.start_date || item.end_date)) {
      goodsItems.push(item);
    } else if (item.start_date || item.end_date) {
      servicesItems.push(item);
    } else {
      const descLower = String(item.description || '').toLowerCase();
      if (CLASSIFY_KEYWORDS.some((kw) => descLower.includes(kw))) {
        servicesItems.push(item);
      } else {
        goodsItems.push(item);
      }
    }
  }
  return [goodsItems, servicesItems];
};

// PHASE A FOLLOW-UP (2026-07-27 ruling): the short, non-blank placeholder
// shown in a VENDOR_PROPOSED line's date cell(s) - the actual
// schedule_note text belongs in Detailed Technical Specifications only,
// never duplicated into a date cell.
export const VENDOR_PROPOSED_CELL_TEXT = 'Vendor proposes';

export const currencyInstruction = (rfpCurrency) =>
  rfpCurrency ? `All prices must be quoted in ${rfpCurrency}.` : 'Please specify currency with your quotation.';

export const scheduleLeadIn = (isMixed, rfpCurrency) => {
  const instruction = currencyInstruction(rfpCurrency);
  return isMixed
    ? `This procurement includes both goods and services. Vendors must complete the Rate and Extended Price columns below. Extended Price = Quantity × Rate. ${instruction}`
    : `Vendors must complete the Rate and Extended Price columns below. Extended Price = Quantity × Rate. ${instruction}`;
};

export const VENDOR_PROPOSED_GOODS_NOTE =
  'Note: For lines marked with a schedule note in place of fixed dates, no delivery/execution schedule has been fixed by the buyer - the vendor is expected to propose one consistent with the required completion date.';

export const VENDOR_PROPOSED_SERVICES_NOTE =
  'Note: For lines marked with a schedule note in place of fixed dates, no execution schedule has been fixed by the buyer - the vendor is expected to propose one consistent with the required completion date.';

// THE POINTER TO SECTION 3 (63e58f9, backend mutant Q5) - added to this
// twin on 2026-08-23, having been missing since 63e58f9 extended
// document_generator.py's _schedule_rates_note and this side was not
// updated. The SAME drift the truncation had, in the same module, found by
// reading the two notes side by side while checking whether the wording
// still reads correctly beside the new "Short Description" header.
//
// It matters more under the derived-name ruling than it did before: the
// schedule cell now carries a NAME and deliberately carries no ellipsis,
// so this note is the ONE place the whole schedule tells a vendor where the
// specification actually lives. Without it the preview showed short names
// and no way to know there was more.
//
// Byte-identical to the Python, asserted from the backend suite.
export const scheduleFooterNote = (isMixed) => {
  const scope = isMixed ? ' in both tables' : '';
  return (
    'Note: Vendors must provide rates and calculate extended prices '
    + `(Rate × Quantity) for each line item${scope}; descriptions in `
    + 'this schedule may be abbreviated, and the full description for each '
    + 'line is given in Detailed Technical Specifications.'
  );
};

// Mirrors document_generator.py's Commercial Requirements Schedule
// table-type resolution: the declared procurement_type seeds the
// decision, but the ACTUAL line composition always wins - both goods
// and services lines present forces a mixed (two-table) schedule
// regardless of the declared type; an empty/unset declared type falls
// back to whichever line type is actually present, defaulting to goods
// when a line has no signal at all.
export const resolveScheduleTypeFlags = (procurementType, lineItems) => {
  let isGoods = procurementType === 'goods';
  let isServices = procurementType === 'services';
  let isMixed = procurementType === 'mixed';

  const items = lineItems || [];
  const hasGoodsItems = items.some(lineIsGoods);
  const hasServicesItems = items.some(lineIsServices);

  if (hasGoodsItems && hasServicesItems) {
    isMixed = true;
    isGoods = false;
    isServices = false;
  } else if (!isGoods && !isServices) {
    if (hasGoodsItems) {
      isGoods = true;
    } else if (hasServicesItems) {
      isServices = true;
    } else {
      isGoods = true;
    }
  }

  return { isGoods, isServices, isMixed };
};
